import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { formatCount } from "@shared-core/domain/formatters";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { useListQuery } from "@shared-core/domain/useListQuery";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import type { CustomerOrderListOptions } from "@shared-core/data/customer-adapter";
import type { CustomerOrderSummary } from "@shared-core/data/customer-api-types";
import { usePortalViewer } from "./PortalViewerContext";
import { useOrderAgainOffered } from "./basket/OrderAgainButton";
import { PortalOrderRow, orderRowKey, orderTitle } from "./PortalOrderRow";
import { PortalOrdersScope, type OrdersView } from "./PortalOrdersScope";
import { PortalHistoryFloor } from "./PortalHistoryFloor";
import { Card } from "../../app/components/primitives/card";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import { cn } from "../../app/components/primitives/utils";
import { Search, Download, Package, AlertTriangle, X } from "lucide-react";
import { currentTime } from "@shared-core/domain/current-time";

// J1 + J3 on one surface (IA option B). "Active" is the default filter, not a
// separate screen — so the glanceable view and the searchable archive are the
// same list in two states.
//
// There is deliberately NO "needs your attention" banner. A portal you must
// visit to discover you are blocking a job is the wrong mechanism; alerting
// belongs to notifications/email. What survives here is URGENCY ORDERING —
// blocked and late work sorts to the top and says so in its status line.
//
// Filter/sort/search/pagination run server-side (docs/list-endpoints.md,
// GET /views/customer/orders) — this page is a thin binding over
// CustomerDataAdapter.listOrders, never a client-side filter/sort/slice.

type Tab = "active" | "all" | "despatched";

const TABS: { id: Tab; label: string }[] = [
  { id: "active", label: "Active" },
  { id: "all", label: "All" },
  { id: "despatched", label: "Despatched" },
];

const PAGE_SIZE = 25;

// Active-tab default ordering: late work first (the urgency ordering of
// 01_product_brief J1), then newest first by the ordered date the row shows —
// never by expected date, which the row does not show and which put a
// just-placed order among older ones. The backend's sortable whitelist
// (customer_orders_query.py) has no awaiting_customer field, so a blocked
// order sorts by its own lateness/date rather than always pinning to the top.
// All and Despatched take the backend default, newest first.
const ACTIVE_SORT = "-is_late,-ordered_at";

function DrillScopeChip({ onClear }: { onClear: () => void }) {
  return (
    <button
      type="button"
      onClick={onClear}
      className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/10"
    >
      Filtered from Spend
      <X className="size-3" />
    </button>
  );
}

export function PortalOrdersPage() {
  const adapter = useCustomerDataAdapter();
  const { viewKey, sessionState, buildPortalUrl } = usePortalViewer();
  const capabilities = usePlatformCapabilities();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  // `?tab=delivered` is the tab's old name: links and bookmarks made before it was renamed still land on it.
  const tab: Tab =
    tabParam === "all"
      ? "all"
      : tabParam === "despatched" || tabParam === "delivered"
        ? "despatched"
        : "active";
  const accountCode = searchParams.get("accountCode") ?? undefined;
  const productCode = searchParams.get("productCode") ?? undefined;
  // "Placed by you": only offered to someone who can see all of the
  // customer's work (PortalOrdersScope); it never widens anyone's list.
  const view: OrdersView = searchParams.get("view") === "mine" ? "mine" : "all";

  const [queryInput, setQueryInput] = useState("");
  const debouncedQuery = useDebouncedValue(queryInput, 300);
  const [page, setPage] = useState(1);

  // Reset to page 1 whenever a filter, the tab, or the search term changes.
  useEffect(() => {
    setPage(1);
  }, [tab, debouncedQuery, accountCode, productCode, view]);

  const { state, retry } = useListQuery(() => {
    const options: CustomerOrderListOptions = {
      status: tab,
      page,
      pageSize: PAGE_SIZE,
      sort: tab === "active" ? ACTIVE_SORT : undefined,
    };
    if (debouncedQuery.trim()) options.q = debouncedQuery.trim();
    if (accountCode) options.accountCode = accountCode;
    if (productCode) options.productCode = productCode;
    if (view === "mine") options.ownWorkOnly = true;
    return adapter.listOrders(options);
  }, [
    adapter,
    viewKey,
    tab,
    page,
    debouncedQuery,
    accountCode,
    productCode,
    view,
  ]);

  // A row only names its site when this page of results actually spans more
  // than one — a single-site customer (or a single-site page of a
  // multi-site one) never needs it repeated on every row.
  const showSiteOnRows =
    state.status === "success" &&
    new Set(
      state.data.items
        .map((o) => o.site_name)
        .filter((s): s is string => Boolean(s)),
    ).size > 1;
  // Room is left for "Order again" on every row of a page where any row can offer it, so the columns line up.
  const orderAgainOffered = useOrderAgainOffered();
  const hasOrderAgainColumn =
    orderAgainOffered &&
    state.status === "success" &&
    state.data.items.some((o) => Boolean(o.reorder_url));

  function selectTab(next: Tab) {
    const params = new URLSearchParams(searchParams);
    if (next === "active") params.delete("tab");
    else params.set("tab", next);
    setSearchParams(params, { replace: true });
  }

  function selectView(next: OrdersView) {
    const params = new URLSearchParams(searchParams);
    if (next === "all") params.delete("view");
    else params.set("view", next);
    setSearchParams(params, { replace: true });
  }

  function clearDrillScope() {
    const params = new URLSearchParams(searchParams);
    params.delete("accountCode");
    params.delete("productCode");
    setSearchParams(params, { replace: true });
  }

  function exportCsv(rows: CustomerOrderSummary[]) {
    const header = [
      "Ordered",
      "Description",
      "Quantity",
      "Reference",
      "Site",
      "Job",
      "Status",
      "Net",
    ];
    const lines = rows.map((o) =>
      [
        o.ordered_at?.slice(0, 10) ?? "",
        orderTitle(o),
        o.quantity !== null && o.quantity !== undefined
          ? String(o.quantity)
          : "",
        o.customer_reference ?? "",
        o.site_name ?? "",
        o.job_no ?? "",
        o.status_line,
        Number(o.net_amount).toFixed(2),
      ]
        .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(","),
    );
    capabilities.downloadTextFile(
      `pollards-orders-${currentTime().toISOString().slice(0, 10)}.csv`,
      [header.join(","), ...lines].join("\n"),
      "text/csv",
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Orders</h1>
          <p className="text-sm text-muted-foreground">
            Everything you have with us, whichever way it was ordered.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {sessionState.status === "success" && (
            <PortalOrdersScope
              session={sessionState.data}
              view={view}
              onViewChange={selectView}
            />
          )}
          {(accountCode || productCode) && (
            <DrillScopeChip onClear={clearDrillScope} />
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-md border p-0.5">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => selectTab(t.id)}
              className={cn(
                "rounded px-3 py-1.5 text-sm font-medium transition-colors",
                tab === t.id
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t.label}
              {t.id === "active" &&
              state.status === "success" &&
              state.data.counts.active > 0
                ? ` ${state.data.counts.active}`
                : ""}
            </button>
          ))}
        </div>
        <div className="relative min-w-[12rem] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            placeholder="Search description, PO or job number"
            className="pl-8"
            aria-label="Search orders"
          />
        </div>
        <Button
          variant="outline"
          onClick={() =>
            state.status === "success" && exportCsv(state.data.items)
          }
          disabled={state.status !== "success" || state.data.items.length === 0}
        >
          <Download className="size-4" />
          Export
        </Button>
      </div>

      {state.status === "loading" && (
        <Card className="flex flex-col items-center gap-2 p-10 text-center">
          <p className="text-sm text-muted-foreground">Loading your orders…</p>
        </Card>
      )}

      {state.status === "error" && (
        <Card className="flex flex-col items-center gap-3 p-10 text-center">
          <AlertTriangle className="size-6 text-muted-foreground" />
          <p className="font-medium">We couldn&apos;t load your orders</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            {state.message}
          </p>
          <Button variant="outline" onClick={retry}>
            Try again
          </Button>
        </Card>
      )}

      {state.status === "success" && state.data.items.length === 0 && (
        <Card className="flex flex-col items-center gap-2 p-10 text-center">
          <Package className="size-6 text-muted-foreground" />
          <p className="font-medium">
            {debouncedQuery
              ? "Nothing matches that search"
              : tab === "active"
                ? "Nothing in progress"
                : "No orders yet"}
          </p>
          <p className="max-w-sm text-sm text-muted-foreground">
            {debouncedQuery
              ? "Try a shorter search, or a PO number on its own."
              : tab === "active"
                ? "When you place an order it will appear here while we make it."
                : "Orders appear here once they have been placed."}
          </p>
        </Card>
      )}

      {state.status === "success" && state.data.items.length > 0 && (
        <>
          {/* One row component for every tab (Active, All, Despatched): the same order reads the same way
              wherever it is listed, and nothing on it needs a horizontal scroll at any width. */}
          <Card className="overflow-hidden p-0">
            <ul aria-label="Orders" className="@container divide-y">
              {state.data.items.map((o) => (
                <PortalOrderRow
                  key={orderRowKey(o)}
                  order={o}
                  buildPortalUrl={buildPortalUrl}
                  showSite={showSiteOnRows}
                  hasOrderAgainColumn={hasOrderAgainColumn}
                />
              ))}
            </ul>
          </Card>
          {tab !== "active" && <PortalHistoryFloor />}

          {state.data.totalPages > 1 && (
            <div className="flex items-center justify-between gap-3 pt-1">
              <p className="text-xs text-muted-foreground">
                Page {state.data.page} of {state.data.totalPages} ·{" "}
                {formatCount(state.data.total)} order
                {state.data.total !== 1 ? "s" : ""}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= state.data.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
